import { isEmptyParagraph } from '@atlaskit/editor-common/utils';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { Transform } from '@atlaskit/editor-prosemirror/transform';
import { canInsert } from '@atlaskit/editor-prosemirror/utils';

/**
 * Positional counterpart to prosemirror-utils' `safeInsert`. That helper reads and writes
 * `tr.selection`, which is undefined on a bare `Transform` (used to build an isolated preview
 * of a suggestion's diff). This walks the same "find the nearest schema-valid ancestor" logic
 * from an explicit position instead, so it works for both `Transaction` and `Transform`.
 *
 * `side` picks which side of the ancestor the content lands on once the walk has to climb past
 * `position`'s immediate parent — e.g. for `insertNodeBefore`, landing "after" the ancestor would
 * silently turn a "before" insert into an "after" one.
 */
export function safeInsertAtPos<T extends Transaction | Transform>(
	tr: T,
	position: number,
	content: PMNode,
	side: 'before' | 'after' = 'after',
): T {
	const $insertPos = tr.doc.resolve(position);

	if (isEmptyParagraph($insertPos.parent) && $insertPos.depth > 0) {
		const $beforeParagraph = tr.doc.resolve($insertPos.before());
		if (canInsert($beforeParagraph, content)) {
			tr.replaceWith($insertPos.before(), $insertPos.after(), content);
			return tr;
		}
	}

	if (canInsert($insertPos, content)) {
		tr.insert($insertPos.pos, content);
		return tr;
	}

	for (let depth = $insertPos.depth; depth > 0; depth--) {
		const pos = side === 'before' ? $insertPos.before(depth) : $insertPos.after(depth);
		if (canInsert(tr.doc.resolve(pos), content)) {
			tr.insert(pos, content);
			return tr;
		}
	}

	return tr;
}
